import type { SubscriptionCategoryOption, SubscriptionFormValues } from '~/shared/components/subscription/types';
import { LIMIT_OF_CHARACTERS } from '~/shared/constants';
import { validate, validator } from '~/shared/helpers/validation-helpers';
import type { FormConfigType } from '~/shared/types/form-type';

const captchaStore = new Map<string, string>();

const getCaptchaCodeSetter = (formName: string) => (code: string) => {
  captchaStore.set(formName, code);
};

const getCaptchaValidator =
  (formName: string, t: (key: string, options?: { ns?: string[] }) => string) => (captcha: string) => {
    const actualCode = captchaStore.get(formName);

    if (actualCode !== captcha) {
      return t('text.not-matched-captcha-code');
    }

    return null;
  };

type GetSubscriptionFormConfigArgs = Readonly<{
  t: (key: string, options?: { ns?: string[] }) => string;
  items: readonly SubscriptionCategoryOption[];
  isCategoriesLoading: boolean;
}>;

const getSubscriptionFormConfig = ({
  items,
  t,
  isCategoriesLoading,
}: GetSubscriptionFormConfigArgs): FormConfigType<SubscriptionFormValues> => {
  const validation = validator({ t });
  const formName = 'subscription';

  return {
    name: formName,
    pattern: 'basicFormPattern',
    textAlign: 'center',
    position4: {
      formFields: [
        {
          name: 'email',
          label: t('field.email.title'),
          validate: validate([
            validation.required,
            validation.isEmail,
            validation.isInRange.bind(null, {
              min: LIMIT_OF_CHARACTERS.EMAIL.MIN,
              max: LIMIT_OF_CHARACTERS.EMAIL.MAX,
            }),
          ]),
          type: 'text',
        },
        {
          name: 'userName',
          label: t('field.user-name.title'),
          validate: validate([
            validation.required,
            validation.isInRange.bind(null, {
              min: LIMIT_OF_CHARACTERS.USER_NAME.MIN,
              max: LIMIT_OF_CHARACTERS.USER_NAME.MAX,
            }),
          ]),
          type: 'text',
        },
        {
          name: 'categories',
          label: t('field.categories.title'),
          type: 'tags-selector',
          config: {
            isClearable: true,
            mode: 'multiple',
            options: items,
            isLoading: isCategoriesLoading,
            onChange: ({ setValue, value }) => {
              setValue('categories', value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
            },
          },
        },
        {
          name: 'captcha',
          label: t('field.captcha.title'),
          type: 'captcha',
          validate: validate([validation.required, getCaptchaValidator(formName, t)]),
          config: {
            getCode: getCaptchaCodeSetter(formName),
          },
        },
        {
          name: 'termsAndConditions',
          type: 'terms-and-conditions',
          validate: (value: boolean) => {
            return value === true ? undefined : t('text.required-field');
          },
          config: {
            t,
          },
        },
      ],
    },
    actions: {
      buttons: [
        {
          name: 'submit',
          htmlType: 'submit',
          size: 'large',
          className: 'call-to-action-button--purple',
          buttonName: t('button.subscription'),
        },
      ],
    },
  };
};

export default getSubscriptionFormConfig;
